import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Paper, Title, Text, Stack as MantineStack, Button, Group, Avatar, Box } from '@mantine/core';
import { Game, Player, Stack, EntryType } from '../types/models';
import PlayerStack from './PlayerStack';
import ResponsivePlayerStack from './ResponsivePlayerStack';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { useViewportSize } from '../hooks/useViewportSize';
import { buildPlayerSlug } from '../utils/playerSlug';

interface GameCompletionProps {
  game: Game;
  currentPlayer: Player;
  stacks: Stack[];
}

const HANDWRITING_FONTS = [
  'Permanent Marker',
  'Caveat',
  'Shadows Into Light',
  'Indie Flower',
  'Gloria Hallelujah',
  'Amatic SC',
  'Rock Salt',
  'Nothing You Could Do',
  'Covered By Your Grace',
  'Just Another Hand',
  'La Belle Aurore',
  'Cedarville Cursive',
  'Waiting for the Sunrise',
];

// Google Fonts URL for loading all handwriting fonts
const GOOGLE_FONTS_URL = `https://fonts.googleapis.com/css2?${HANDWRITING_FONTS.map(
  font => `family=${font.replace(/ /g, '+')}:wght@400`
).join('&')}&display=swap`;

const GameCompletion: React.FC<GameCompletionProps> = ({
  game,
  currentPlayer,
  stacks
}) => {
  const { isMobile } = useResponsiveLayout();
  const viewport = useViewportSize();
  
  // Load Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.href = GOOGLE_FONTS_URL;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    return () => {
      // Don't remove the link as other components might need it
    };
  }, []);
  
  // Calculate optimal polaroid scale
  const calculatePolaroidScale = () => {
    if (isMobile) return 1;
    
    const maxScale = 2.5;
    const minScale = 1;
    const baseSize = 140; // Base polaroid width including gaps
    const padding = 40; // Total horizontal padding
    const gap = 32; // Gap between polaroids (xl gap)
    
    // Use full viewport width minus padding
    const containerWidth = viewport.width - padding;
    
    // Determine optimal number of columns based on viewport
    let columns = 4;
    if (viewport.width < 800) columns = 2;
    else if (viewport.width < 1000) columns = 3;
    
    // Don't use more columns than we have players
    columns = Math.min(columns, game.players.length);
    
    // Calculate available width per polaroid
    const totalGapWidth = (columns - 1) * gap;
    const availableWidthPerPolaroid = (containerWidth - totalGapWidth) / columns;
    
    // Calculate scale to fit available space
    const scale = Math.min(maxScale, Math.max(minScale, availableWidthPerPolaroid / 120));
    
    return scale;
  };
  
  const polaroidScale = calculatePolaroidScale();
  // Group entries by player to show statistics
  const playerStats = game.players.map(player => {
    const textEntries = stacks.reduce((count, stack) => 
      count + stack.expectedEntries.filter(entry => 
        entry.completed && 
        entry.expectedPlayerId === player.id && 
        entry.entryType === EntryType.TEXT
      ).length, 0);

    const drawingEntries = stacks.reduce((count, stack) => 
      count + stack.expectedEntries.filter(entry => 
        entry.completed && 
        entry.expectedPlayerId === player.id && 
        entry.entryType === EntryType.DRAWING
      ).length, 0);

    return {
      player,
      textEntries,
      drawingEntries,
      totalEntries: textEntries + drawingEntries
    };
  });

  return (
    <Box py="xl">
      <MantineStack gap="xl" align="center">
          {/* Header */}
          <MantineStack gap="sm" align="center">
            <Title order={1} ta="center">🎉 Done!</Title>
            <Text size="lg" c="dimmed" ta="center">
              Check out everyone's stacks
            </Text>
          </MantineStack>

          {/* Player Stack Links - Polaroid Style */}
          <MantineStack gap="sm" align="center">
            {/* <Text fw={600} ta="center" mb="md">View Each Player's Stack:</Text> */}
            
            <Group justify="center" gap="xl" w="100%">
              {game.players.map((player) => {
                const isCurrentPlayer = player.id === currentPlayer.id;
                return (
                  <Box
                    key={player.id}
                    component={Link}
                    to={`/games/${game.key}/${buildPlayerSlug(player)}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <MantineStack gap="md" align="center" style={{ 
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                    }}>
                      {/* Player Stack */}
                      <Box 
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0px)';
                        }}
                      >
                        <ResponsivePlayerStack
                          avatar={player.avatarUrl}
                          name={player.name}
                          scale={polaroidScale}
                          handwritingFont={player.handwritingFont}
                        />
                      </Box>
                    </MantineStack>
                  </Box>
                );
              })}
            </Group>
          </MantineStack>

          {/* Quick Stats */}
          <Box ta="center">
            <Text size="sm" c="dimmed">
              {game.players.length} players • {stacks.length} stacks completed
            </Text>
          </Box>
      </MantineStack>
    </Box>
  );
};

export default GameCompletion; 
